import { computationOptions } from "#app/computationOptions";
import {
  isNestingOptimization,
  isProcessOptimization,
} from "#modules/optimizations/domain/optimizationFilters";
import { OptimizationDto } from "#modules/optimizations/infra/computeOptimizations.api";
import {
  computeMaterialPrice,
  computeNestingMaterialGain,
  computeNestingUnitMaterialCosts,
  computeOptimizedMachineTimes,
  getNestedLineQuantity,
  getOptimizationMachineProcessSteps,
  NO_OPTIMIZED_MATERIAL_COSTS,
  sumMachineTimesOptimizationGain,
} from "shared";

const MIN_DISPLAYED_GAIN = 0.005;

export const hasDisplayableGain = (gain: number | null) =>
  gain !== null && gain >= MIN_DISPLAYED_GAIN;

const computeNestingGain = (optimization: OptimizationDto): number | null => {
  const metadata = optimization.sheetNestingOptimizationMetadata;
  if (!metadata?.layout || !metadata.nestingRun) {
    return null;
  }

  const lines = optimization.quoteRequestLines.map((line) => ({
    id: line.id,
    quantity: getNestedLineQuantity(line),
  }));
  const currentUnitCosts = new Map(
    optimization.quoteRequestLines.flatMap((line) =>
      line.layout
        ? [
            [
              line.id,
              computeMaterialPrice(
                {
                  quoteRequestLine: { ...line, layout: line.layout },
                  optimizedMaterialCosts: NO_OPTIMIZED_MATERIAL_COSTS,
                },
                computationOptions,
              ).partMaterialCost,
            ] as const,
          ]
        : [],
    ),
  );

  return computeNestingMaterialGain({
    lines,
    currentUnitCosts,
    nestedUnitCosts: computeNestingUnitMaterialCosts({
      nestingRun: metadata.nestingRun,
      layout: metadata.layout,
      lines,
    }),
  });
};

const computeProcessGain = (optimization: OptimizationDto): number | null => {
  if (!optimization.machineTimesOptimizationMetadata) {
    return null;
  }

  const optimizedMachineTimes = computeOptimizedMachineTimes([{ ...optimization, enabled: true }]);

  return sumMachineTimesOptimizationGain({
    lines: getOptimizationMachineProcessSteps(optimization).map(({ line, step }) => ({
      quantity: line.quantity,
      quantityInAssembly: line.part.quantityInAssembly,
      reworkRatio: line.reworkRatio ?? null,
      processStep: step,
    })),
    optimizedMachineTimes,
  });
};

const computeOptimizationGain = (optimization: OptimizationDto): number | null => {
  if (isProcessOptimization(optimization)) {
    return computeProcessGain(optimization);
  }

  if (isNestingOptimization(optimization)) {
    return computeNestingGain(optimization);
  }

  return null;
};

export const getOptimizationGain = (optimization: OptimizationDto) => {
  if (optimization.deletedAt) {
    return { gain: null, hasGain: false, isCalculated: false };
  }

  const gain = computeOptimizationGain(optimization);
  const hasGain = hasDisplayableGain(gain);
  const isCalculated =
    gain !== null &&
    (isNestingOptimization(optimization) || (isProcessOptimization(optimization) && hasGain));

  return { gain, hasGain, isCalculated };
};

export const sumAppliedOptimizationsGain = (optimizations: OptimizationDto[]) =>
  optimizations.reduce(
    (total, optimization) =>
      optimization.enabled && !optimization.deletedAt
        ? total + (computeOptimizationGain(optimization) ?? 0)
        : total,
    0,
  );
